
import { Helmet } from "react-helmet-async";

interface SEOTagsProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  path?: string;
  type?: "website" | "product" | "article";
  structuredData?: Record<string, any>;
}

export const SEOTags = ({
  title,
  description,
  keywords,
  ogImage = "/og-image.png",
  path = "",
  type = "website",
  structuredData,
}: SEOTagsProps) => {
  const baseUrl = "https://roselandsni.com";
  const fullUrl = `${baseUrl}${path}`;
  const defaultKeywords = "pet supplies Northern Ireland, dog toys Belfast, dog treats Lurgan, pet store NI, pet products Armagh, dog accessories Banbridge, pet shop Portadown, Northern Ireland pet supplies, UK pet store, premium dog products";

  // Append NI to title if not already present
  const formattedTitle = title.includes("NI") ? title : `${title} | Roselands NI`;
  
  // Enhanced structured data for the website with improved SEO elements
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "PetStore",
    "name": "Roselands NI",
    "description": description,
    "url": fullUrl,
    "logo": `${baseUrl}/lovable-uploads/02f3f3ff-b2c3-48d2-b7e3-2a0eaf22e87d.png`,
    "image": `${baseUrl}${ogImage}`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Waringstown",
      "addressRegion": "Northern Ireland",
      "addressCountry": "GB",
      "postalCode": "BT66 7QX"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "54.4633",
      "longitude": "-6.3350"
    },
    "telephone": "+447490377878",
    "email": "info@roselandsni.com",
    "priceRange": "££",
    "currenciesAccepted": "GBP",
    "paymentAccepted": "Credit Card, Debit Card, PayPal",
    "areaServed": [
      "Northern Ireland",
      "Belfast",
      "Banbridge",
      "Lurgan",
      "Portadown",
      "Armagh",
      "Craigavon"
    ],
    "serviceType": "Pet Supplies",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Pet Supplies Catalog",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Dog Food & Treats",
            "category": "Pet Food"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Cat Food & Treats",
            "category": "Pet Food"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Pet Toys & Accessories",
            "category": "Pet Accessories"
          }
        }
      ]
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday"
        ],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Saturday"
        ],
        "opens": "10:00",
        "closes": "17:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/roselandsni",
      "https://www.instagram.com/roselandsni"
    ]
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{formattedTitle}</title>
      <meta name="title" content={formattedTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content="Roselands NI" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content="Roselands NI" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={formattedTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@roselandsni" />

      {/* Location */}
      <meta name="geo.region" content="GB-NIR" />
      <meta name="geo.placename" content="Waringstown, Northern Ireland" />
      <meta name="geo.position" content="54.4633;-6.3350" />
      <meta name="ICBM" content="54.4633, -6.3350" />

      {/* Additional Meta */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="UK" />
      <meta name="coverage" content="Northern Ireland" />
      <meta name="target" content="Northern Ireland" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#f4797c" />

      {/* Structured Data JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};
